import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { showApiError, showSuccess } from "@/lib/toast";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import React from "react";
import { useTranslation } from "react-i18next";
import { ContentGroupMember, RobotEmotion, RobotAge, RobotGender } from "@/lib/types";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { updateContentGroupMembers } from "@/sideEffects/api";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { LoadingSmall } from "@/components/LoadingSmall";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContentGroupMemberCategorySelectGuid } from "../contentGroupMemberCategory/ContentGroupMemberCategorySelectGuid";

const AgeTranslation: Record<RobotAge, string> = {
  [RobotAge.A0]: "N/A",
  [RobotAge.A018]: "0-18",
  [RobotAge.A1929]: "19-29",
  [RobotAge.A3039]: "30-39",
  [RobotAge.A4049]: "40-49",
  [RobotAge.A5059]: "50-59",
  [RobotAge.A60100]: "60+",
};

const EmotionTranslation: Record<RobotEmotion, string> = {
  [RobotEmotion.E0]: "N/A",
  [RobotEmotion.happy]: "happy",
  [RobotEmotion.neutral]: "neutral",
  [RobotEmotion.sad]: "sad",
  [RobotEmotion.surprize]: "surprize",
  [RobotEmotion.angry]: "angry",
};

interface Props {
  contentGroupMember: ContentGroupMember;
  onOpenChange: (open: boolean) => void;
  showRobotControls: boolean;
}

const formSchema = z.object({
  duration: z.string(),
  order: z.string(),
  isActive: z.boolean(),
  age: z.string(),
  emotion: z.string(),
  gender: z.string(),
  categoryId: z.string(),
});

type FormSchemaType = z.infer<typeof formSchema>;

export const UpdateContentGroupMemberDialog: React.FC<Props> = ({
  contentGroupMember,
  onOpenChange,
  showRobotControls,
}) => {
  const activeFirm = useSelector(selectActiveFirm);
  const { t } = useTranslation();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      duration: String(contentGroupMember.duration),
      order: String(contentGroupMember.order),
      isActive: contentGroupMember.isActive,
      age: String(contentGroupMember.cameraAge),
      emotion: String(contentGroupMember.cameraEmotion),
      gender: String(contentGroupMember.gender),
      categoryId: contentGroupMember.contentGroupMemberCategoryGuid || "",
    },
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (values: FormSchemaType) =>
      updateContentGroupMembers(
        String(activeFirm?.firmGuid),
        contentGroupMember.contentGroupMemberGuid,
        // @ts-ignore
        values
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contentGroupMembers"],
      });
      showSuccess(t("common.updatedSuccess"));
      onOpenChange(false);
    },
    onError: showApiError,
  });

  function onSubmit(values: FormSchemaType) {
    mutation.mutate(values);
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.entityDetail", { name: t("contentGroup.contentMember") })}</DialogTitle>
          <DialogDescription>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("contentGroup.duration")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("contentGroup.duration")} type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("contentGroup.isActive")}</FormLabel>
                      <FormControl>
                        <div>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="order"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("common.order")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("common.order")} type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {showRobotControls && (
                  <div className="grid grid-cols-2 gap-4 border-t pt-4">
                    <FormField
                      control={form.control}
                      name="age"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("common.age")}</FormLabel>
                          <FormControl>
                            <Select defaultValue={field.value} onValueChange={(value) => field.onChange(value)}>
                              <SelectTrigger className="w-full h-[32px]">
                                <SelectValue placeholder={t("common.type")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={RobotAge.A0.toString()}>{AgeTranslation[RobotAge.A0]}</SelectItem>
                                <SelectItem value={RobotAge.A018.toString()}>
                                  {AgeTranslation[RobotAge.A018]}
                                </SelectItem>
                                <SelectItem value={RobotAge.A1929.toString()}>
                                  {AgeTranslation[RobotAge.A1929]}
                                </SelectItem>
                                <SelectItem value={RobotAge.A3039.toString()}>
                                  {AgeTranslation[RobotAge.A3039]}
                                </SelectItem>
                                <SelectItem value={RobotAge.A4049.toString()}>
                                  {AgeTranslation[RobotAge.A4049]}
                                </SelectItem>
                                <SelectItem value={RobotAge.A5059.toString()}>
                                  {AgeTranslation[RobotAge.A5059]}
                                </SelectItem>
                                <SelectItem value={RobotAge.A60100.toString()}>
                                  {AgeTranslation[RobotAge.A60100]}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="emotion"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("common.emotion")}</FormLabel>
                          <FormControl>
                            <Select defaultValue={field.value} onValueChange={(value) => field.onChange(value)}>
                              <SelectTrigger className="w-full h-[32px]">
                                <SelectValue placeholder={t("common.emotion")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={RobotEmotion.E0.toString()}>
                                  {EmotionTranslation[RobotEmotion.E0]}
                                </SelectItem>
                                <SelectItem value={RobotEmotion.happy.toString()}>
                                  {EmotionTranslation[RobotEmotion.happy]}
                                </SelectItem>
                                <SelectItem value={RobotEmotion.neutral.toString()}>
                                  {EmotionTranslation[RobotEmotion.neutral]}
                                </SelectItem>
                                <SelectItem value={RobotEmotion.sad.toString()}>
                                  {EmotionTranslation[RobotEmotion.sad]}
                                </SelectItem>
                                <SelectItem value={RobotEmotion.surprize.toString()}>
                                  {EmotionTranslation[RobotEmotion.surprize]}
                                </SelectItem>
                                <SelectItem value={RobotEmotion.angry.toString()}>
                                  {EmotionTranslation[RobotEmotion.angry]}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("user.gender")}</FormLabel>
                          <FormControl>
                            <Select defaultValue={field.value} onValueChange={(value) => field.onChange(value)}>
                              <SelectTrigger className="w-full h-[32px]">
                                <SelectValue placeholder={t("common.type")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={RobotGender.Male.toString()}>{t("user.male")}</SelectItem>
                                <SelectItem value={RobotGender.Female.toString()}>{t("user.female")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="categoryId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Category")}</FormLabel>
                          <FormControl>
                            <ContentGroupMemberCategorySelectGuid field={field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
                <Button type="submit" disabled={mutation.isPending}>
                  {t("common.update")}
                  {mutation.isPending && <LoadingSmall />}
                </Button>
              </form>
            </Form>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
